import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Timer, Inbox, Radio, FileCheck2 } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";

const SITE = "https://oceandata.ca";

const PAINS = [
  {
    title: "La pregunta espera en un buzón",
    body: "La obra pregunta a las 7:12. La pregunta espera a que la persona correcta la note — a menudo después del almuerzo, a veces tras dos recordatorios. Mientras tanto, el equipo en obra espera o adivina.",
  },
  {
    title: "La respuesta se pierde entre la obra y la oficina",
    body: "Reenviada, respondida, e imposible de encontrar tres semanas después cuando alguien pregunta «¿quién aprobó esto?». La respuesta existía. Solo vivía en el lugar equivocado.",
  },
  {
    title: "La misma pregunta vuelve",
    body: "Sin expediente central, el mismo RFI vuelve de otra obra, de otro teléfono. Cada respuesta se reinventa en lugar de reutilizarse.",
  },
];

const FLOW = [
  {
    n: "01",
    title: "Enviado una vez",
    body: "El RFI entra por un solo canal — de la obra o de la oficina — y recibe un número, un responsable y una fecha. Se acabaron los hilos de correo.",
  },
  {
    n: "02",
    title: "Visible por la persona correcta, de inmediato",
    body: "La pregunta llega directamente a quien puede responderla, con el plano y el contexto adjuntos. Nadie transporta la información a mano.",
  },
  {
    n: "03",
    title: "Respondido en el mismo expediente",
    body: "La respuesta vive con la pregunta, visible para todos los que la necesitan — hoy, y en tres semanas cuando alguien pregunte quién aprobó qué.",
  },
];

const FAQ = [
  {
    q: "¿Esto reemplaza nuestro software de gestión de obra?",
    a: "No. Conectamos lo que ya usa — Zoho Projects, Microsoft 365, Google, Autodesk — para que el RFI tenga un solo hogar. Su equipo conserva sus hábitos; la información deja de perderse entre ellos.",
  },
  {
    q: "¿Qué significa «en horas»?",
    a: "En Construction Horizon, el tiempo de respuesta a los RFI cayó 43 % en seis semanas — medido en sus propios registros. Las decisiones ahora toman horas, no días. Su número depende de su punto de partida; lo medimos juntos.",
  },
  {
    q: "¿Cómo empezar sin perturbar las obras en curso?",
    a: "Con una revisión gratuita de 15 minutos, luego un solo proceso a la vez — practicado primero en una copia segura de sus datos. Ningún cambio en producción antes de que el equipo esté listo.",
  },
];

export default function EsRfiPage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Respuesta a los RFI en horas, no en días | Ocean Data — Miami — Montréal";
    document.documentElement.lang = "es";
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = "es";
    el.href = `${SITE}/es/respuesta-rfi-en-horas`;
    el.setAttribute("data-hreflang-injected", "true");
    document.head.appendChild(el);
    return () => {
      document.documentElement.lang = "en";
      el.remove();
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/es/respuesta-rfi-en-horas`,
        name: "Respuesta a los RFI en horas, no en días",
        description:
          "Para empresas de construcción: RFI enviados una vez, visibles por la persona correcta de inmediato, respondidos en el mismo expediente. 43 % más rápido en Construction Horizon — medido.",
        inLanguage: "es",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "Service",
        name: "Conexión del proceso RFI — construcción",
        provider: { "@id": `${SITE}/#org` },
        areaServed: [
          { "@type": "City", name: "Miami" },
          { "@type": "City", name: "Montréal" },
        ],
        audience: { "@type": "BusinessAudience", name: "Empresas de construcción" },
        description:
          "Conectamos las herramientas que ya usa para que cada solicitud de información (RFI) se envíe una vez, sea visible de inmediato por la persona correcta y se responda en el mismo expediente.",
      },
      {
        "@type": "FAQPage",
        inLanguage: "es",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang="es">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Miami — Montréal · Construcción
          </p>
          <h1
            data-testid="esrfi-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="esrfi-headline-line-1">Respuesta a los RFI en horas,</MaskedLine>
            <MaskedLine delay={0.3} testId="esrfi-headline-line-2">
              <span className="text-brand-teal">no en días.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            El RFI decide si la obra avanza o espera. Conectamos las
            herramientas que ya tiene para que cada pregunta se envíe una vez,
            la vea la persona correcta y se responda en el mismo expediente.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="esrfi-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              Muéstreme dónde se atasca
              <ArrowRight size={18} />
            </button>
            <p className="text-sm text-slate-500">Revisión gratuita de 15 minutos · Sin compromiso</p>
          </motion.div>
        </div>
      </section>

      {/* El verdadero costo */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            El verdadero costo de un RFI lento
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            No es la pregunta lo que cuesta. Es la espera.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {PAINS.map((p, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div
                  data-testid={`esrfi-pain-${i}`}
                  className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <span className="w-9 h-9 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center mb-4">
                    <Inbox size={16} />
                  </span>
                  <h3 className="font-display font-semibold text-lg text-brand-navy leading-snug mb-3">{p.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* La versión conectada */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            La versión conectada
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Un solo hogar para cada pregunta.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {FLOW.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div data-testid={`esrfi-step-${i}`} className="rounded-2xl bg-white border border-slate-200 p-7 sm:p-8 h-full">
                  <p className="font-mono text-sm text-brand-teal font-semibold mb-4">{s.n}</p>
                  <h3 className="font-display font-semibold text-xl text-brand-navy mb-3">{s.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Prueba Horizon */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-10">
            Medido, no prometido
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-14">
            {[
              ["43 %", "menos tiempo de respuesta a los RFI en Construction Horizon"],
              ["6 semanas", "de la primera llamada al resultado medido"],
              ["1 proceso", "conectado — sin cambiar las herramientas del equipo"],
            ].map(([num, label], i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div data-testid={`esrfi-proof-${i}`}>
                  <p className="font-display font-bold text-4xl sm:text-5xl text-white mb-2">{num}</p>
                  <p className="text-sm text-brand-pale leading-relaxed">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-brand-navysurf border border-white/10 p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <div className="flex items-start gap-4 max-w-2xl">
                <Radio size={20} className="text-brand-gold shrink-0 mt-1" />
                <div>
                  <p className="font-display font-medium text-lg leading-snug text-white mb-2">
                    «En seis semanas, el tiempo de respuesta de nuestro equipo a los RFI cayó 43 %. Las decisiones ahora toman horas, no días.»
                  </p>
                  <p className="text-xs text-slate-400">Director de Operaciones, Construction Horizon</p>
                </div>
              </div>
              <Link
                to="/es/journal/horizon-construction-caso-de-estudio"
                data-testid="esrfi-case-study-link"
                className="shrink-0 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 text-brand-gold text-sm font-semibold px-6 py-3 transition-all duration-300 hover:bg-brand-gold hover:text-brand-navy"
              >
                <FileCheck2 size={15} /> Leer el caso de estudio
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Preguntas frecuentes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Lo que nos preguntan los contratistas
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`esrfi-faq-${i}`} className="group rounded-2xl bg-white border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Su próximo RFI podría responderse antes del almuerzo.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Quince minutos para mapear cómo viajan sus RFI hoy — y ver
                  exactamente dónde se pierden las horas.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 shrink-0">
                <button
                  data-testid="esrfi-cta-final"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
                >
                  <Timer size={18} />
                  Solicitar mi revisión gratuita
                </button>
                <p className="text-xs text-slate-400">
                  Todas sus herramientas se quedan:{" "}
                  <Link to="/es/mantenga-sus-herramientas" className="text-brand-gold hover:text-white transition-colors">
                    las conectamos, no reemplazamos nada
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
